import { ColumnFilterModel } from '../models/common/ColumnFilterModel';
import { foldText } from '../components/ColumnFilter/ColumnFilterTypes';

/**
 * Liste tek seferde gelen standart liste ekranlarında (sunucu ucu sayfalama yapmıyorsa) arama, kolon filtresi,
 * sıralama ve sayfalamanın ekranda uygulanması (18.10.2026; Özel Günler'deki kalıbın ortak hali, Ek Mesai Talepleri kullanır).
 *
 * kolonDegeri: süzme / sıralama için satırın kolon değeri. Seçimli kolonda filtre değeriyle aynı (ör. "PLAN"),
 * tarih kolonunda "yyyy-MM-dd", sayı kolonunda sayı döner.
 */
export type KolonDegeri<T> = (satir: T, kolon: string) => string | number | null | undefined;

const metin = (v: string | number | null | undefined) => v == null ? '' : String(v);

/** Arama kutusu: verilen kolonlardan birinde geçen kayıtlar (Türkçe harf duyarsız). */
export const ekrandaAra = <T>(liste: T[], arama: string | null | undefined, kolonlar: string[], kolonDegeri: KolonDegeri<T>): T[] => {
    var a = foldText((arama ?? '').trim());

    if (!a)
        return liste;

    return liste.filter(x => kolonlar.some(k => foldText(metin(kolonDegeri(x, k))).includes(a)));
}

/** Kolon filtreleri: metin içerir, seçim listede, tarih / sayı aralıkta (uçlar dahil). */
export const ekrandaSuz = <T>(liste: T[], filtreler: ColumnFilterModel[] | null | undefined, kolonDegeri: KolonDegeri<T>): T[] =>
    liste.filter(x => (filtreler ?? []).every(f => {
        var v = kolonDegeri(x, f.column);

        if (f.values && f.values.length > 0)
            return f.values.includes(metin(v));

        if (f.text && f.text.trim())
            return foldText(metin(v)).includes(foldText(f.text.trim()));

        if (f.from || f.to) {
            if (v == null || v === '')
                return false;

            if (typeof v == 'number') {
                var alt = f.from ? Number(f.from.replace(',', '.')) : undefined;
                var ust = f.to ? Number(f.to.replace(',', '.')) : undefined;
                return (alt == undefined || isNaN(alt) || v >= alt) && (ust == undefined || isNaN(ust) || v <= ust);
            }

            var d = metin(v).substring(0, 10);
            return (!f.from || d >= f.from) && (!f.to || d <= f.to);
        }

        return true;
    }));

/** Sıralama: sayılar sayı olarak, diğerleri Türkçe metin olarak; boşlar sona. Kararlı (eşitlerde gelen sıra korunur). */
export const ekrandaSirala = <T>(liste: T[], kolon: string | null | undefined, yon: string | null | undefined, kolonDegeri: KolonDegeri<T>): T[] => {
    if (!kolon)
        return liste;

    var carpan = yon == 'desc' ? -1 : 1;

    return liste
        .map((x, i) => ({ x, i, v: kolonDegeri(x, kolon) }))
        .sort((a, b) => {
            var bosA = a.v == null || a.v === '', bosB = b.v == null || b.v === '';

            if (bosA || bosB)
                return bosA && bosB ? a.i - b.i : bosA ? 1 : -1;

            var r = typeof a.v == 'number' && typeof b.v == 'number' ? a.v - b.v : metin(a.v).localeCompare(metin(b.v), 'tr');
            return r != 0 ? r * carpan : a.i - b.i;
        })
        .map(z => z.x);
}

/** Sayfa: skip / take. */
export const ekrandaSayfala = <T>(liste: T[], skip: number, take: number): T[] => liste.slice(Math.max(0, skip), Math.max(0, skip) + Math.max(1, take));
